import { Component, Injector, ViewEncapsulation } from '@angular/core';
import { ClassNames, DateFormat, getDynamicRouteLink, StatusColor } from '@lsq-frontend/common-ui';
import {
    ClaimService,
    getRouterLinkUrlById,
    Icons,
    ResponsiveService
} from '@lsq-frontend/common-ui';
import { LsqApiServiceListSource } from '@lsq-frontend/angular-shared/api-service';
import { AnalyticsValue } from '../../../constants/analytics.constants';
import { EnumDisplay } from '../../../constants/enum-display.constants';
import { atlas, CUSTOMER_OVERVIEW } from '@lsq-frontend/fasttrack-shared/routing-atlas';
import {
    GetCreditRequestsRequest,
    GetCreditRequestsService
} from '@lsq-frontend/remote-services/enterprise/dashboard-services';
import { DateRangeReportComponent } from '../base/date-range-report.component';
import { ReportsUserStateTypes } from '../reports.user-state-type';
import {
    DashboardClaim,
    CreditRequestStatus,
    GetCreditRequestsResponse,
    CreditRequest
} from '@lsq-frontend/remote-services/enterprise/common';

@Component({
    selector: 'credit-request-history',
    template: `
        <div
            [ngClass]="{ 'min-page-width-medium-up': !(dataSource?.loading$ | async) }"
            class="credit-request-history-page"
        >
            <lsq-banner
                [parentClass]="classNames.MobileBannerDateFilter"
                [titleText]="title"
                [subTitle]="(dataSource.data$ | async)?.lastUpdatedDate | lastUpdatedDate"
                [hasContent]="true"
                [mobileTitleMultiline]="true"
                [reportName]="title"
                [analyticsName]="analyticsName"
                [service]="apiService"
                [hideExport]="dataSource.noData$ | async"
                [datePickerConfiguration]="datePickerConfiguration"
            >
            </lsq-banner>
            <div class="container-fluid page-container">
                <div class="row">
                    <div class="col">
                        <lsq-data-table
                            *ngIf="!(responsiveService.smBelow | async)"
                            [dataSource]="dataSource"
                            [isPaginated]="true"
                            [hasStickyHeader]="true"
                            (takeChanged)="handleTakeChange($event)"
                        >
                            <lsq-row-count-column></lsq-row-count-column>

                            <lsq-date-column
                                [column]="{
                                    header: 'Date',
                                    key: 'startDate',
                                    className: 'start-date'
                                }"
                            >
                            </lsq-date-column>

                            <lsq-column
                                [column]="{
                                    header: 'Customer',
                                    key: 'debtorName',
                                    truncateTextCss: true,
                                    className: 'customer-name-link'
                                }"
                            >
                                <ng-template [custom-cell] let-row="row">
                                    <a
                                        *ngIf="canViewCustomers"
                                        [routerLink]="
                                            getCustomerLink(
                                                overviewByCustomerRouterLinkUrl,
                                                row.debtorKey
                                            )
                                        "
                                        class="preserve-case"
                                        [queryParams]="{ date: asOfDateQueryString }"
                                    >
                                        {{ row.debtorName }}
                                    </a>
                                    <span *ngIf="!canViewCustomers">
                                        {{ row.debtorName }}
                                    </span>
                                </ng-template>
                            </lsq-column>

                            <lsq-currency-column
                                [column]="{
                                    header: 'Amount<br/>Requested',
                                    key: 'requestedAmount',
                                    className: 'requested-amount'
                                }"
                            >
                            </lsq-currency-column>

                            <lsq-currency-column
                                [column]="{
                                    header: 'Amount<br/>Approved',
                                    key: 'approvedAmount',
                                    className: 'approved-amount'
                                }"
                            >
                            </lsq-currency-column>

                            <lsq-column
                                [column]="{
                                    header: 'Status',
                                    key: 'creditRequestStatus',
                                    className: 'credit-request-status'
                                }"
                            >
                                <ng-template [custom-cell] let-row="row">
                                    <lsq-icon
                                        *ngIf="row.creditRequestStatus"
                                        [style.padding-right]="'0.5rem'"
                                        [type]="icons.Circle"
                                        [color]="
                                            statusColor[
                                                creditRequestStatus[row.creditRequestStatus]
                                            ]
                                        "
                                    ></lsq-icon>
                                    <span class="status-text">{{
                                        creditRequestStatusDisplay[row.creditRequestStatus]
                                    }}</span>
                                </ng-template>
                            </lsq-column>

                            <lsq-column
                                [column]="{
                                    header: 'Reason',
                                    key: 'reason',
                                    className: 'reason'
                                }"
                            >
                            </lsq-column>
                        </lsq-data-table>

                        <lsq-data-table-mobile
                            *ngIf="responsiveService.smBelow | async"
                            [dataSource]="dataSource"
                            [isPaginated]="true"
                        >
                            <ng-template #mobileCardHeader let-row="row">
                                <div class="credit-request-history-mobile-card-header">
                                    <div class="mtc-header-content-left">
                                        <div class="mtc-header-super-title">
                                            {{ row.debtorName }}
                                        </div>
                                        <div class="mtc-header-title">
                                            {{ row.requestedAmount | lsqCurrency }}
                                        </div>
                                        <div class="mtc-header-sub-title">
                                            {{ row.startDate | date: dateFormat.LsqShortYear }}
                                        </div>
                                    </div>
                                    <div class="mtc-header-content-right">
                                        <div class="mtc-header-status-text">
                                            <span class="status-text">{{
                                                creditRequestStatusDisplay[row.creditRequestStatus]
                                            }}</span>
                                            <lsq-icon
                                                *ngIf="row.creditRequestStatus"
                                                [style.padding-right]="'0.5rem'"
                                                [type]="icons.Circle"
                                                [color]="
                                                    statusColor[
                                                        creditRequestStatus[row.creditRequestStatus]
                                                    ]
                                                "
                                            >
                                            </lsq-icon>
                                        </div>
                                    </div>
                                </div>
                            </ng-template>

                            <ng-template #mobileCardColumns let-row="row">
                                <div class="card-item">
                                    <div class="card-item-label">
                                        Customer
                                    </div>
                                    <div class="card-item-value">
                                        <a
                                            *ngIf="canViewCustomers"
                                            [routerLink]="
                                                getCustomerLink(
                                                    overviewByCustomerRouterLinkUrl,
                                                    row.debtorKey
                                                )
                                            "
                                            class="preserve-case"
                                            [queryParams]="{ date: asOfDateQueryString }"
                                        >
                                            {{ row.debtorName }}
                                        </a>
                                        <span *ngIf="!canViewCustomers">
                                            {{ row.debtorName }}
                                        </span>
                                    </div>
                                </div>

                                <div class="card-item">
                                    <div class="card-item-label">
                                        Amount Requested
                                    </div>
                                    <div class="card-item-value">
                                        {{ row.requestedAmount | lsqCurrency }}
                                    </div>
                                </div>

                                <div class="card-item">
                                    <div class="card-item-label">
                                        Amount Approved
                                    </div>
                                    <div class="card-item-value">
                                        {{ row.approvedAmount | lsqCurrency }}
                                    </div>
                                </div>

                                <div class="card-item">
                                    <div class="card-item-label">
                                        Reason
                                    </div>
                                    <div class="card-item-value">
                                        {{ row.reason }}
                                    </div>
                                </div>
                            </ng-template>
                        </lsq-data-table-mobile>
                    </div>
                </div>
            </div>
        </div>
    `,
    styleUrls: ['./credit-request-history.report.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class CreditRequestHistoryReportComponent extends DateRangeReportComponent {
    public title = 'Credit Request History';
    public savedDateFilterName = null;
    public analyticsName = AnalyticsValue.CreditRequestHistory;
    public userStateType = ReportsUserStateTypes.GetCreditRequestsRequest;
    public canViewCustomers = this._claimService.isOnSync(DashboardClaim.ViewCustomers);

    // Constants.
    public classNames = ClassNames;
    public creditRequestStatus = CreditRequestStatus;
    public creditRequestStatusDisplay = EnumDisplay.CreditRequestStatus;
    public dateFormat = DateFormat;
    public icons = Icons;
    public overviewByCustomerRouterLinkUrl = getRouterLinkUrlById(atlas, CUSTOMER_OVERVIEW);
    public statusColor = StatusColor;

    public dataSource = new LsqApiServiceListSource<
        GetCreditRequestsResponse,
        GetCreditRequestsRequest,
        CreditRequest
    >(this.apiService);

    constructor(
        public _claimService: ClaimService,
        public apiService: GetCreditRequestsService,
        public responsiveService: ResponsiveService,
        public injector: Injector
    ) {
        super(injector);

        this.dispatchInitialRequest();
    }

    public getCustomerLink(link: string, debtorKey: string): string {
        return getDynamicRouteLink(link, { debtorKey });
    }
}
